import React, { useEffect, useState, lazy } from 'react';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Select } from '../components/Select';
import { TerminalSquare, User, Briefcase, MapPin, ArrowRight, CheckCircle2, Loader2, ShieldCheck, Cpu, MenuIcon, XIcon } from 'lucide-react';
interface OnboardPageProps {
  onNavigate?: (path: string) => void;
}
const cities = [{
  id: 'benin',
  name: 'Benin',
  whatsapp: 'https://chat.whatsapp.com/Gg0acDUlz9WKb1bu4EPeEf'
}, {
  id: 'lagos',
  name: 'Lagos',
  whatsapp: 'https://chat.whatsapp.com/BmhDckI1FiEJxVxNUXQbKh'
}, {
  id: 'asaba',
  name: 'Asaba',
  whatsapp: 'https://chat.whatsapp.com/DkaarUKWs7aJ6crRvDPOpt'
}, {
  id: 'uyo',
  name: 'Uyo',
  whatsapp: 'https://chat.whatsapp.com/IJAovNsQlYb0lqR9wGcBvL'
}, {
  id: 'abuja',
  name: 'Abuja',
  whatsapp: 'https://chat.whatsapp.com/KyuLep5qjEwHU6VR8tiBW9'
}, {
  id: 'calabar',
  name: 'Calabar',
  whatsapp: 'https://chat.whatsapp.com/placeholder-calabar'
}, {
  id: 'ogbomosho',
  name: 'Ogbomosho',
  whatsapp: 'https://chat.whatsapp.com/placeholder-ogbomosho'
}, {
  id: 'warri',
  name: 'Warri',
  whatsapp: 'https://chat.whatsapp.com/placeholder-warri'
}, {
  id: 'port-harcourt',
  name: 'Port Harcourt',
  whatsapp: 'https://chat.whatsapp.com/placeholder-portharcourt'
}, {
  id: 'canada',
  name: 'Canada',
  whatsapp: 'https://chat.whatsapp.com/EoH9cpC9aKf3UpVkypIJ8U'
}];
export function OnboardPage({
  onNavigate
}: OnboardPageProps) {
  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    brandName: '',
    expertise: '',
    cityId: ''
  });
  // Auto-advance from intro screen
  useEffect(() => {
    if (step === 0) {
      const timer = setTimeout(() => setStep(1), 2500);
      return () => clearTimeout(timer);
    }
  }, [step]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleCityChange = (value: string | string[]) => {
    setFormData(prev => ({
      ...prev,
      cityId: value as string
    }));
  };
  const nextStep = () => {
    if (step === 1 && (!formData.fullName || !formData.email || !formData.phone)) return;
    if (step === 2 && (!formData.brandName || !formData.expertise)) return;
    if (step === 3 && !formData.cityId) return;
    setStep(prev => prev + 1);
  };
  const prevStep = () => {
    setStep(prev => prev - 1);
  };
  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const selectedCity = cities.find(c => c.id === formData.cityId);
      const payload = {
        type: 'Onboarding',
        ...formData,
        cityName: selectedCity?.name
      };
      // Submit to both Formspree and Google Sheets in parallel
      await Promise.all([fetch('https://formspree.io/f/mpwyowlw', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      }), fetch('https://script.google.com/macros/s/AKfycbzR_-KmqKZFGGbKzHfTDJ1Shbo07C0RSOwx4RKTH9sU4PjA-r7elviN739j-03EfNIa/exec', {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })]);
      // Simulate network delay for futuristic effect
      setTimeout(() => {
        setIsSubmitting(false);
        setStep(5); // Success step
      }, 1500);
    } catch (error) {
      console.error('Submission error:', error);
      setIsSubmitting(false);
      // Proceed to success anyway for UX in this demo
      setStep(5);
    }
  };
  const selectedCityData = cities.find(c => c.id === formData.cityId);
  return <div className="min-h-screen bg-[#1f2933] text-white font-sans overflow-hidden flex flex-col selection:bg-salon-red/30" data-id="element-1581">
      {/* Custom CSS for Futuristic Animations */}
      <style data-id="element-1582">{`
        .grid-bg {
          background-size: 40px 40px;
          background-image: 
            linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
          mask-image: radial-gradient(circle at center, black 40%, transparent 100%);
        }
        
        @keyframes scanline {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100vh); }
        }
        .scan-line {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 8px;
          background: linear-gradient(to bottom, transparent, rgba(237, 28, 36, 0.5), transparent);
          animation: scanline 6s linear infinite;
          pointer-events: none;
          z-index: 50;
        }

        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 15px rgba(237, 28, 36, 0.2), inset 0 0 10px rgba(237, 28, 36, 0.1); }
          50% { box-shadow: 0 0 25px rgba(237, 28, 36, 0.4), inset 0 0 20px rgba(237, 28, 36, 0.2); }
        }
        .glass-panel {
          background: rgba(31, 41, 51, 0.7);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(237, 28, 36, 0.3);
          animation: pulse-glow 4s ease-in-out infinite;
        }

        .terminal-text {
          font-family: 'JetBrains Mono', monospace;
        }

        /* Custom scrollbar for the select dropdown to match dark theme */
        ::-webkit-scrollbar {
          width: 6px;
        }
        ::-webkit-scrollbar-track {
          background: #1f2933;
        }
        ::-webkit-scrollbar-thumb {
          background: #ED1C24;
          border-radius: 3px;
        }
      `}</style>

      {/* Background Elements */}
      <div className="absolute inset-0 grid-bg z-0" data-id="element-1583" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-salon-red/20 rounded-full blur-[120px] z-0" data-id="element-1584" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-salon-red/10 rounded-full blur-[120px] z-0" data-id="element-1585" />
      <div className="scan-line" data-id="element-1586" />

      {/* SALON-STYLE NAVBAR */}
      <nav className="sticky top-0 z-50 w-full bg-[#1f2933] text-white shadow-md" data-id="element-1587">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8" data-id="element-1588">
          {/* Logo */}
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => onNavigate && onNavigate('/salon')} data-id="element-1589">
            <img src="https://cdn.magicpatterns.com/uploads/dKtihEU4NjCWDRS7TF9QX8/2-3_100310.png" alt="The Networking Salon" loading="lazy" className="h-20 w-auto brightness-150 contrast-125" data-id="element-1590" />
          </div>

          {/* Desktop Links */}
          <div className="hidden lg:flex items-center gap-8" data-id="element-1591">
            <button onClick={() => onNavigate && onNavigate('/salon')} className="text-sm font-bold hover:text-salon-red transition-colors" data-id="element-1592">
              ABOUT THE SALON
            </button>
            <Button onClick={() => onNavigate && onNavigate('/salon#cities')} className="bg-salon-red hover:bg-salon-red/90 border-salon-red text-white font-bold rounded-full px-6" data-id="element-1593">
              PAYMENT
            </Button>
          </div>

          {/* Mobile Menu Toggle */}
          <button className="lg:hidden text-white p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} data-id="element-1594">
            {isMobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-1595" /> : <MenuIcon className="w-6 h-6" data-id="element-1596" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && <div className="lg:hidden absolute top-20 left-0 w-full bg-[#1f2933] border-t border-gray-700 p-4 flex flex-col gap-4 shadow-xl" data-id="element-1597">
            <button onClick={() => {
          onNavigate && onNavigate('/salon');
          setIsMobileMenuOpen(false);
        }} className="text-left text-sm font-bold p-2 hover:text-salon-red" data-id="element-1598">
              ABOUT THE SALON
            </button>
            <Button onClick={() => {
          onNavigate && onNavigate('/salon#cities');
          setIsMobileMenuOpen(false);
        }} className="bg-salon-red border-salon-red text-white w-full mt-2" data-id="element-1599">
              PAYMENT
            </Button>
          </div>}
      </nav>

      {/* Main Content */}
      <main className="relative z-10 flex-1 flex items-center justify-center p-4 sm:p-8" data-id="element-1600">
        <div className="w-full max-w-2xl" data-id="element-1601">
          {/* Step 0: Initialization */}
          {step === 0 && <div className="flex flex-col items-center justify-center space-y-6 animate-in fade-in zoom-in duration-700" data-id="element-1602">
              <Cpu className="w-20 h-20 text-salon-red animate-pulse" data-id="element-1603" />
              <h1 className="text-2xl md:text-4xl font-bold tracking-widest text-transparent bg-clip-text bg-gradient-to-r from-white to-salon-red" data-id="element-1604">
                INITIALIZING PROTOCOL
              </h1>
              <div className="w-64 h-1 bg-gray-800 rounded-full overflow-hidden" data-id="element-1605">
                <div className="h-full bg-salon-red animate-[pulse_1s_ease-in-out_infinite] w-full origin-left scale-x-0 animate-[scale-x_2s_ease-out_forwards]" style={{
              animationName: 'progress'
            }} data-id="element-1606"></div>
              </div>
              <style data-id="element-1607">{`@keyframes progress { to { transform: scaleX(1); } }`}</style>
              <p className="terminal-text text-sm text-gray-400 animate-pulse" data-id="element-1608">
                Establishing secure handshake...
              </p>
            </div>}

          {/* Form Steps Container */}
          {step > 0 && step < 5 && <div className="glass-panel rounded-2xl p-6 md:p-10 shadow-2xl shadow-salon-red/10 animate-in slide-in-from-bottom-8 fade-in duration-500" data-id="element-1609">
              {/* Progress Indicator */}
              <div className="flex items-center justify-between mb-10 border-b border-white/10 pb-6" data-id="element-1610">
                <div className="flex items-center gap-2" data-id="element-1611">
                  <TerminalSquare className="w-5 h-5 text-salon-red" data-id="element-1612" />
                  <span className="terminal-text text-sm font-bold tracking-widest text-salon-red" data-id="element-1613">
                    STEP 0{step} <span className="text-gray-500" data-id="element-1614">/ 04</span>
                  </span>
                </div>
                <div className="flex gap-1" data-id="element-1615">
                  {[1, 2, 3, 4].map(i => <div key={i} className={`h-1.5 w-8 rounded-full transition-all duration-300 ${i === step ? 'bg-salon-red shadow-[0_0_8px_#ED1C24]' : i < step ? 'bg-salon-red/50' : 'bg-gray-800'}`} data-id="element-1616" />)}
                </div>
              </div>

              {/* Step 1: Personal Info */}
              {step === 1 && <div className="space-y-6 animate-in slide-in-from-right-8 fade-in duration-300" data-id="element-1617">
                  <div className="mb-8" data-id="element-1618">
                    <h2 className="text-3xl font-bold mb-2 flex items-center gap-3" data-id="element-1619">
                      <User className="w-8 h-8 text-salon-red" data-id="element-1620" />
                      Identification
                    </h2>
                    <p className="text-gray-400 terminal-text text-sm" data-id="element-1621">
                      Enter your primary telemetry data.
                    </p>
                  </div>

                  <div className="space-y-5" data-id="element-1622">
                    <div data-id="element-1623">
                      <label className="block terminal-text text-xs text-salon-red mb-2 tracking-widest" data-id="element-1624">
                        FULL_NAME
                      </label>
                      <input type="text" name="fullName" value={formData.fullName} onChange={handleInputChange} className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-salon-red focus:ring-1 focus:ring-salon-red outline-none transition-all placeholder:text-gray-600 font-mono" placeholder="John Doe" data-id="element-1625" />
                    </div>
                    <div data-id="element-1626">
                      <label className="block terminal-text text-xs text-salon-red mb-2 tracking-widest" data-id="element-1627">
                        EMAIL_ADDRESS
                      </label>
                      <input type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-salon-red focus:ring-1 focus:ring-salon-red outline-none transition-all placeholder:text-gray-600 font-mono" placeholder="john@example.com" data-id="element-1628" />
                    </div>
                    <div data-id="element-1629">
                      <label className="block terminal-text text-xs text-salon-red mb-2 tracking-widest" data-id="element-1630">
                        COMM_LINK (PHONE)
                      </label>
                      <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-salon-red focus:ring-1 focus:ring-salon-red outline-none transition-all placeholder:text-gray-600 font-mono" placeholder="+234 800 000 0000" data-id="element-1631" />
                    </div>
                  </div>
                </div>}

              {/* Step 2: Brand Info */}
              {step === 2 && <div className="space-y-6 animate-in slide-in-from-right-8 fade-in duration-300" data-id="element-1632">
                  <div className="mb-8" data-id="element-1633">
                    <h2 className="text-3xl font-bold mb-2 flex items-center gap-3" data-id="element-1634">
                      <Briefcase className="w-8 h-8 text-salon-red" data-id="element-1635" />
                      Brand Telemetry
                    </h2>
                    <p className="text-gray-400 terminal-text text-sm" data-id="element-1636">
                      Define your operational parameters.
                    </p>
                  </div>

                  <div className="space-y-5" data-id="element-1637">
                    <div data-id="element-1638">
                      <label className="block terminal-text text-xs text-salon-red mb-2 tracking-widest" data-id="element-1639">
                        BRAND/BUSINESS_NAME
                      </label>
                      <input type="text" name="brandName" value={formData.brandName} onChange={handleInputChange} className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-salon-red focus:ring-1 focus:ring-salon-red outline-none transition-all placeholder:text-gray-600 font-mono" placeholder="Acme Corp" data-id="element-1640" />
                    </div>
                    <div data-id="element-1641">
                      <label className="block terminal-text text-xs text-salon-red mb-2 tracking-widest" data-id="element-1642">
                        CORE_EXPERTISE / BIO
                      </label>
                      <textarea name="expertise" value={formData.expertise} onChange={handleInputChange} rows={4} className="w-full bg-black/40 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-salon-red focus:ring-1 focus:ring-salon-red outline-none transition-all placeholder:text-gray-600 font-mono resize-none" placeholder="What is your primary function?" data-id="element-1643" />
                    </div>
                  </div>
                </div>}

              {/* Step 3: City Selection */}
              {step === 3 && <div className="space-y-6 animate-in slide-in-from-right-8 fade-in duration-300" data-id="element-1644">
                  <div className="mb-8" data-id="element-1645">
                    <h2 className="text-3xl font-bold mb-2 flex items-center gap-3" data-id="element-1646">
                      <MapPin className="w-8 h-8 text-salon-red" data-id="element-1647" />
                      Node Selection
                    </h2>
                    <p className="text-gray-400 terminal-text text-sm" data-id="element-1648">
                      Select your physical connection point.
                    </p>
                  </div>

                  <div className="space-y-5" data-id="element-1649">
                    <div data-id="element-1650">
                      <label className="block terminal-text text-xs text-salon-red mb-2 tracking-widest" data-id="element-1651">
                        AVAILABLE_COMMUNITIES
                      </label>

                      {/* Custom styled select for dark theme */}
                      <div className="relative" data-id="element-1652">
                        <select value={formData.cityId} onChange={e => handleCityChange(e.target.value)} className="w-full bg-black/60 border border-salon-red/50 rounded-lg px-4 py-4 text-white focus:border-salon-red focus:ring-2 focus:ring-salon-red/50 outline-none transition-all font-mono appearance-none cursor-pointer hover:bg-black/80 shadow-[0_0_15px_rgba(237,28,36,0.1)]" data-id="element-1653">
                          <option value="" disabled className="bg-gray-900 text-gray-500" data-id="element-1654">
                            -- SELECT A NODE --
                          </option>
                          {cities.map(city => <option key={city.id} value={city.id} className="bg-gray-900 text-white py-2" data-id="element-1655">
                              {city.name.toUpperCase()}
                            </option>)}
                        </select>
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-1656">
                          <div className="w-3 h-3 border-b-2 border-r-2 border-salon-red transform rotate-45" data-id="element-1657"></div>
                        </div>
                      </div>

                      {formData.cityId && <div className="mt-6 p-4 bg-salon-red/10 border border-salon-red/30 rounded-lg flex items-start gap-3 animate-in fade-in" data-id="element-1658">
                          <CheckCircle2 className="w-5 h-5 text-salon-red flex-shrink-0 mt-0.5" data-id="element-1659" />
                          <div data-id="element-1660">
                            <p className="text-sm font-bold text-white" data-id="element-1661">
                              Node Verified:{' '}
                              {cities.find(c => c.id === formData.cityId)?.name}
                            </p>
                            <p className="text-xs text-gray-400 mt-1 terminal-text" data-id="element-1662">
                              Connection stable. Ready for finalization.
                            </p>
                          </div>
                        </div>}
                    </div>
                  </div>
                </div>}

              {/* Step 4: Processing */}
              {step === 4 && <div className="py-12 flex flex-col items-center justify-center space-y-6 animate-in fade-in duration-300" data-id="element-1663">
                  <div className="relative" data-id="element-1664">
                    <div className="w-24 h-24 border-4 border-gray-800 rounded-full" data-id="element-1665"></div>
                    <div className="w-24 h-24 border-4 border-salon-red rounded-full border-t-transparent animate-spin absolute top-0 left-0" data-id="element-1666"></div>
                    <ShieldCheck className="w-8 h-8 text-salon-red absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-1667" />
                  </div>
                  <h2 className="text-2xl font-bold tracking-widest text-white" data-id="element-1668">
                    PROCESSING DATA
                  </h2>
                  <div className="terminal-text text-xs text-salon-red space-y-1 text-center" data-id="element-1669">
                    <p className="animate-pulse" data-id="element-1670">&gt; Encrypting payload...</p>
                    <p className="animate-pulse" style={{
                animationDelay: '0.5s'
              }} data-id="element-1671">
                      &gt; Establishing secure uplink...
                    </p>
                    <p className="animate-pulse" style={{
                animationDelay: '1s'
              }} data-id="element-1672">
                      &gt; Generating access keys...
                    </p>
                  </div>
                </div>}

              {/* Navigation Buttons */}
              {step < 4 && <div className="mt-10 flex gap-4 pt-6 border-t border-white/10" data-id="element-1673">
                  {step > 1 && <Button variant="secondary" onClick={prevStep} className="flex-1 bg-transparent border border-white/20 text-white hover:bg-white/5 rounded-lg terminal-text tracking-widest" data-id="element-1674">
                      &lt; BACK
                    </Button>}
                  <Button onClick={step === 3 ? handleSubmit : nextStep} disabled={step === 1 && (!formData.fullName || !formData.email || !formData.phone) || step === 2 && (!formData.brandName || !formData.expertise) || step === 3 && !formData.cityId} className="flex-[2] bg-salon-red hover:bg-salon-red/80 border-salon-red text-white rounded-lg terminal-text tracking-widest shadow-[0_0_15px_rgba(237,28,36,0.3)] hover:shadow-[0_0_25px_rgba(237,28,36,0.5)] transition-all disabled:opacity-50 disabled:shadow-none" data-id="element-1675">
                    {step === 3 ? 'INITIALIZE LINK' : 'CONTINUE >'}
                  </Button>
                </div>}
            </div>}

          {/* Step 5: Success / Access Granted */}
          {step === 5 && selectedCityData && <div className="glass-panel rounded-2xl p-8 md:p-12 text-center shadow-[0_0_30px_rgba(237,28,36,0.2)] animate-in zoom-in-95 fade-in duration-700" data-id="element-1676">
              <div className="w-20 h-20 bg-salon-red/20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-[0_0_20px_rgba(237,28,36,0.4)]" data-id="element-1677">
                <CheckCircle2 className="w-10 h-10 text-salon-red" data-id="element-1678" />
              </div>

              <h2 className="text-3xl md:text-4xl font-black mb-2 tracking-tight text-white" data-id="element-1679">
                ACCESS GRANTED
              </h2>
              <p className="terminal-text text-salon-red mb-8 tracking-widest text-sm" data-id="element-1680">
                NODE: {selectedCityData.name.toUpperCase()}
              </p>

              <div className="bg-black/40 border border-white/10 rounded-xl p-6 mb-8 text-left" data-id="element-1681">
                <p className="text-gray-300 mb-4 leading-relaxed" data-id="element-1682">
                  Your telemetry has been recorded. To complete your integration
                  and receive payment details, join the secure comms channel
                  below.
                </p>
                <div className="flex items-center gap-3 text-sm text-gray-400 terminal-text bg-black/50 p-3 rounded border border-gray-800" data-id="element-1683">
                  <div className="w-2 h-2 rounded-full bg-salon-red animate-pulse" data-id="element-1684"></div>
                  Awaiting your connection...
                </div>
              </div>

              <a href={selectedCityData.whatsapp} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 w-full bg-white text-black hover:bg-gray-200 rounded-xl px-8 py-4 font-bold text-lg transition-all shadow-[0_0_20px_rgba(255,255,255,0.2)] hover:shadow-[0_0_30px_rgba(255,255,255,0.4)] hover:scale-[1.02]" data-id="element-1685">
                JOIN WHATSAPP COMMUNITY
                <ArrowRight className="w-5 h-5" data-id="element-1686" />
              </a>

              <button onClick={() => onNavigate && onNavigate('/')} className="mt-6 terminal-text text-xs text-gray-500 hover:text-white transition-colors underline underline-offset-4" data-id="element-1687">
                RETURN TO MAIN TERMINAL
              </button>
            </div>}
        </div>
      </main>
    </div>;
}